import { Component } from '@theme/component';

/**
 * A custom element that manages the Availity insurance and warehouse checker functionality.
 * 
 * Handles insurance form validation, API calls for eligibility checking, warehouse validation,
 * session storage for data persistence, skip insurance functionality, and add-to-cart button state management.
 *
 * @typedef {object} AvailityInsuranceWarehouseCheckerRefs
 * @property {HTMLDivElement} insuranceSection - The insurance form section
 * @property {HTMLDivElement} warehouseSection - The warehouse checker section
 * @property {HTMLInputElement} insuranceIdInput - The insurance ID input field
 * @property {HTMLInputElement} firstNameInput - The first name input field
 * @property {HTMLInputElement} lastNameInput - The last name input field
 * @property {HTMLSelectElement} dobMonth - The date of birth month select
 * @property {HTMLInputElement} dobDayInput - The date of birth day input field
 * @property {HTMLInputElement} dobYearInput - The date of birth year input field
 * @property {HTMLInputElement} warehouseInput - The warehouse number input field
 * @property {HTMLButtonElement} insuranceSubmitButton - The insurance submit button
 * @property {HTMLButtonElement} warehouseSubmitButton - The warehouse submit button
 * @property {HTMLSpanElement} insuranceSubmitButtonText - The insurance submit button text
 * @property {HTMLSpanElement} warehouseSubmitButtonText - The warehouse submit button text
 * @property {HTMLSpanElement} insuranceSubmitButtonSpinner - The insurance submit button spinner
 * @property {HTMLSpanElement} warehouseSubmitButtonSpinner - The warehouse submit button spinner
 * @property {HTMLDivElement} notificationArea - The notification area
 * @property {HTMLAnchorElement} skipInsuranceLink - The skip insurance link
 * @property {HTMLDivElement} insuranceFormFields - The insurance form fields container
 * @property {HTMLDivElement} insuranceSkippedMessage - The insurance skipped message
 * Note: Hidden fields for line item properties are dynamically injected into the product form
 * @property {HTMLDivElement | undefined} debugSection - The debug section (if enabled)
 * @property {HTMLAnchorElement | undefined} skipCheckLink - The skip check link (if debug mode)
 * @extends Component<AvailityInsuranceWarehouseCheckerRefs>
 */
export class AvailityInsuranceWarehouseCheckerComponent extends Component {
  requiredRefs = ['notificationArea'];

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #debounceTimeout;

  /** @type {boolean} */
  #insuranceValidated = false;

  /** @type {boolean} */
  #warehouseValidated = false;

  /** @type {boolean} */
  #insuranceSkipped = false;

  /** @type {string} */
  #insuranceApiUrl = 'https://store-signin.wiivvapi.com/api/wiivvAuth/validate2';

  /** @type {string[]} */
  #validWarehouses = ['119', '120', '140', '485', '640', '687', '1038', '335', '363', '364', '365'];

  /** @type {string} */
  #displayMode = 'full';

  /** @type {boolean} */
  #eligibilityCheckEnabled = true;

  connectedCallback() {
    super.connectedCallback();

    // Initialize configuration
    this.#initializeConfiguration();

    // Find the actual input elements within the animated input fields
    this.#setupInputReferences();

    // Set up event listeners
    this.#setupEventListeners();
    
    // Initialize state
    this.#initializeState();
    
    // Inject hidden inputs into product form for line item properties
    this.#injectHiddenInputsIntoProductForm();
    
    // Handle URL parameters and session storage
    this.#handleDataPopulation();
    
    // Set up global functions for compatibility
    this.#setupGlobalFunctions();
    
    // Listen for variant updates to ensure measurement validation works with theme's existing logic
    this.#setupVariantListeners();
    
    // Monitor measurement checker state if it exists
    if (document.querySelector('measurement-checker-component')) {
      this.#startMeasurementStateMonitoring();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    
    this.#abortController.abort();
    
    if (this.#debounceTimeout) {
      clearTimeout(this.#debounceTimeout);
    }
  }

  /**
   * Initialize configuration from data attributes
   */
  #initializeConfiguration() {
    this.#displayMode = this.dataset.displayMode || 'full';
    this.#insuranceApiUrl = this.dataset.insuranceApiUrl || this.#insuranceApiUrl;
    this.#eligibilityCheckEnabled = this.dataset.enableEligibilityCheck !== 'false';
    
    const validWarehousesData = this.dataset.validWarehouses;
    if (validWarehousesData) {
      this.#validWarehouses = validWarehousesData.split(',').map(w => w.trim());
    }
  }

  /**
   * Set up input references for the animated input fields
   */
  #setupInputReferences() {
    // Helper function to get actual input from animated input wrapper
    const getActualInput = (wrapperRef) => {
      if (!this.refs[wrapperRef]) return null;
      const wrapper = this.refs[wrapperRef];
      const actualInput = wrapper.querySelector && wrapper.querySelector('.animated-input-field__input');
      if (actualInput) {
        this.refs[wrapperRef] = actualInput;
        return actualInput;
      }
      return wrapper;
    };

    // Update refs for insurance fields (if they exist)
    if (this.#displayMode !== 'warehouse_only') {
      getActualInput('insuranceIdInput');
      getActualInput('firstNameInput');
      getActualInput('lastNameInput');
      getActualInput('dobDayInput');
      getActualInput('dobYearInput');
    }

    // Update refs for warehouse field (if it exists)
    if (this.#displayMode !== 'insurance_only') {
      getActualInput('warehouseInput');
    }
  }

  /**
   * Get the current input element (handles dynamic references)
   */
  #getInput(refName) {
    const element = this.refs[refName];
    if (!element) return null;
    
    // Ensure we always have the correct reference to the actual input
    if (element.classList && element.classList.contains('animated-input-field__input')) {
      return element;
    }
    
    // If the reference is still pointing to the wrapper, find the actual input
    const actualInput = element.querySelector && element.querySelector('.animated-input-field__input');
    if (actualInput) {
      this.refs[refName] = actualInput;
      return actualInput;
    }
    
    return element;
  }

  /**
   * Set up event listeners for the component
   */
  #setupEventListeners() {
    const { signal } = this.#abortController;

    // Insurance submit button click (only if eligibility check is enabled)
    if (this.refs.insuranceSubmitButton && this.#eligibilityCheckEnabled) {
      this.refs.insuranceSubmitButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.#handleInsuranceSubmit();
      }, { signal });
    }

    // Warehouse submit button click
    if (this.refs.warehouseSubmitButton) {
      this.refs.warehouseSubmitButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.#handleWarehouseSubmit();
      }, { signal });
    }

    // Skip insurance link
    if (this.refs.skipInsuranceLink) {
      this.refs.skipInsuranceLink.addEventListener('click', this.#handleSkipInsurance.bind(this), { signal });
    }

    // Skip check link (debug mode)
    if (this.refs.skipCheckLink) {
      this.refs.skipCheckLink.addEventListener('click', this.#handleSkipCheck.bind(this), { signal });
    }

    // Insurance field changes with debouncing
    if (this.#displayMode !== 'warehouse_only') {
      ['insuranceIdInput', 'firstNameInput', 'lastNameInput', 'dobDayInput', 'dobYearInput'].forEach(refName => {
        const input = this.#getInput(refName);
        if (input) {
          input.addEventListener('input', this.#handleInsuranceFieldInput.bind(this), { signal });
          input.addEventListener('keydown', this.#handleKeyDown.bind(this), { signal });
        }
      });

      if (this.refs.dobMonth) {
        this.refs.dobMonth.addEventListener('change', this.#handleInsuranceFieldInput.bind(this), { signal });
      }
    }

    // Warehouse field changes
    if (this.#displayMode !== 'insurance_only') {
      const warehouseInput = this.#getInput('warehouseInput');
      if (warehouseInput) {
        warehouseInput.addEventListener('input', this.#handleWarehouseFieldInput.bind(this), { signal });
        warehouseInput.addEventListener('keydown', this.#handleKeyDown.bind(this), { signal });
      }
    }
  }

  /**
   * Initialize component state
   */
  #initializeState() {
    // Initially disable add to cart button if the setting is enabled
    this.#updateAddToCartButtonState();
    
    // Set up global state variables for compatibility
    window.availityInsuranceValidated = this.#insuranceValidated;
    window.availityWarehouseValidated = this.#warehouseValidated;
    window.warehouseCheckRequired = this.#insuranceSkipped;
    
    // Don't set window.checkEnableAddToCart here - let #setupGlobalFunctions handle it
    // This prevents overriding the measurement checker's global coordinator
  }

  /**
   * Inject hidden inputs into the product form for line item properties
   * Following the same pattern as the Scan ID implementation
   */
  #injectHiddenInputsIntoProductForm() {
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (!productForm) return;

    const properties = [
      ...(this.#displayMode !== 'warehouse_only' ? [
        'Insurance ID', 'First Name', 'Last Name', 'Date of Birth', '_insuranceSkipped'
      ] : []),
      ...(this.#displayMode !== 'insurance_only' ? ['_warehouseNumberUsed'] : [])
    ];
    
    properties.forEach(property => {
      if (!productForm.querySelector(`input[name="properties[${property}]"]`)) {
        const input = Object.assign(document.createElement('input'), {
          type: 'hidden',
          name: `properties[${property}]`,
          value: property === '_insuranceSkipped' ? 'false' : ''
        });
        productForm.appendChild(input);
      }
    });
  }

  /**
   * Update a form-injected hidden input value
   * @param {string} propertyName - The property name
   * @param {string} value - The value to set
   */
  #updateFormInjectedInput(propertyName, value) {
    const input = document.querySelector(`form[action*="/cart/add"] input[name="properties[${propertyName}]"]`);
    if (input) input.value = value;
  }

  /**
   * Handle data population from URL parameters and session storage
   */
  #handleDataPopulation() {
    const autoPopulate = this.dataset.autoPopulateFromUrl === 'true';
    const sessionPersistence = this.dataset.sessionPersistence === 'true';
    const autoSubmit = this.dataset.autoSubmitOnLoad === 'true';

    if (autoPopulate) {
      this.#populateFromUrl();
    }

    if (sessionPersistence) {
      this.#populateFromSession(autoSubmit);
    }

    // Check for insurance skipped status
    this.#checkSkippedStatus();
  }

  /**
   * Populate fields from URL parameters
   */
  #populateFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const warehouseParam = params.get('w');
    
    if (warehouseParam) {
      try {
        this.#skipInsurance();
      } catch (error) {
        console.error('Error processing w query parameter:', error);
      }
    }
  }

  /**
   * Populate fields from session storage
   * @param {boolean} autoSubmit - Whether to automatically submit
   */
  #populateFromSession(autoSubmit) {
    // Check insurance data
    if (this.#displayMode !== 'warehouse_only') {
      const storedInsuranceData = sessionStorage.getItem('eligibilityFormData');
      if (storedInsuranceData) {
        try {
          const formData = JSON.parse(storedInsuranceData);
          const insuranceIdInput = this.#getInput('insuranceIdInput');
          const firstNameInput = this.#getInput('firstNameInput');
          const lastNameInput = this.#getInput('lastNameInput');
          const dobDayInput = this.#getInput('dobDayInput');
          const dobYearInput = this.#getInput('dobYearInput');

          if (insuranceIdInput && formData.insuranceId) insuranceIdInput.value = formData.insuranceId;
          if (firstNameInput && formData.firstName) firstNameInput.value = formData.firstName;
          if (lastNameInput && formData.lastName) lastNameInput.value = formData.lastName;
          
          if (formData.dob) {
            const [year, month, day] = formData.dob.split('-');
            if (this.refs.dobMonth && month) this.refs.dobMonth.value = month;
            if (dobDayInput && day) dobDayInput.value = day;
            if (dobYearInput && year) dobYearInput.value = year;
          }

          this.#updateInsuranceHiddenFields();

          if (autoSubmit) {
            setTimeout(() => {
              this.#handleInsuranceSubmit();
            }, 100);
          }
        } catch (error) {
          console.error('Error parsing stored insurance data:', error);
        }
      }
    }

    // Check warehouse data
    if (this.#displayMode !== 'insurance_only') {
      const storedWarehouseData = sessionStorage.getItem('pdpWarehouseChecker');
      if (storedWarehouseData) {
        try {
          const warehouseData = JSON.parse(storedWarehouseData);
          const warehouseInput = this.#getInput('warehouseInput');
          if (warehouseInput && warehouseData.warehouse) {
            warehouseInput.value = warehouseData.warehouse;
            this.#updateWarehouseHiddenFields();
          }
        } catch (error) {
          console.error('Error parsing stored warehouse data:', error);
        }
      }
    }
  }

  /**
   * Check if insurance was previously skipped
   */
  #checkSkippedStatus() {
    const skippedStatus = sessionStorage.getItem('insuranceSkipped');
    if (skippedStatus === 'true') {
      this.#skipInsurance();
    }
  }

  /**
   * Handle insurance field input changes with debouncing
   */
  #handleInsuranceFieldInput() {
    // Clear any existing timeout
    if (this.#debounceTimeout) {
      clearTimeout(this.#debounceTimeout);
    }
    
    // Clear any existing field errors when user starts typing
    this.#clearFieldErrors();
    
    // Update hidden fields immediately
    this.#updateInsuranceHiddenFields();
    
    // Update add-to-cart button state immediately
    this.#updateAddToCartButtonState();
    
    // Debounce the session storage update
    this.#debounceTimeout = setTimeout(() => {
      this.#updateInsuranceInSession();
    }, 300);
  }

  /**
   * Handle warehouse field input changes
   */
  #handleWarehouseFieldInput() {
    // Clear any existing timeout
    if (this.#debounceTimeout) {
      clearTimeout(this.#debounceTimeout);
    }
    
    // Update hidden fields immediately
    this.#updateWarehouseHiddenFields();
    
    // Reset validation state
    this.#warehouseValidated = false;
    this.#updateAddToCartButtonState();
    
    // Clear notification
    this.refs.notificationArea.innerHTML = '';
    this.refs.notificationArea.style.display = 'none';
    
    // Debounce the session storage update
    this.#debounceTimeout = setTimeout(() => {
      this.#updateWarehouseInSession();
    }, 100);
  }

  /**
   * Handle keydown events
   * @param {KeyboardEvent} event - The keydown event
   */
  #handleKeyDown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();

      const target = event.target;

      // If the field declares enterkeyhint="next", advance focus instead of submitting
      if (target.enterKeyHint === 'next') {
        const section = target.closest('.availity-checker__insurance-section');
        if (section) {
          const focusable = [...section.querySelectorAll('input, select')].filter(
            (el) => !el.disabled && el.offsetParent !== null
          );
          const idx = focusable.indexOf(target);
          if (idx !== -1 && idx < focusable.length - 1) {
            target.blur();
            focusable[idx + 1].focus();
            return;
          }
        }
      }

      target.blur();

      // Only the warehouse field submits on Enter — insurance fields just
      // dismiss the keyboard so the user can scroll to the auth checkbox.
      if (target.closest('.availity-checker__warehouse-section') && this.refs.warehouseSubmitButton) {
        this.#handleWarehouseSubmit();
      }
    }
  }

  /**
   * Handle skip insurance link click
   * @param {Event} event - The click event
   */
  #handleSkipInsurance(event) {
    event.preventDefault();
    
    // Check if insurance is already skipped
    if (this.#insuranceSkipped) {
      this.#restartInsurance();
    } else {
      this.#skipInsurance();
    }
  }

  /**
   * Handle skip check link click (debug mode)
   * @param {Event} event - The click event
   */
  #handleSkipCheck(event) {
    event.preventDefault();
    
    // Mark validation as complete
    this.#insuranceValidated = true;
    this.#warehouseValidated = true;
    
    // Update add to cart button state
    this.#updateAddToCartButtonState();
    
    // Show success notification
    this.#displayNotification({
      type: 'success',
      title: 'Skip Validation Enabled',
      content: 'You can now add items to your cart. (Debug mode active)'
    });
  }

  /**
   * Skip insurance and enable warehouse checker
   */
  #skipInsurance() {
    this.#insuranceSkipped = true;
    
    // Reset warehouse validation state when switching to warehouse mode
    this.#warehouseValidated = false;
    
    // Hide insurance form fields
    if (this.refs.insuranceFormFields) {
      this.refs.insuranceFormFields.classList.add('hidden');
    }
    
    // Show skipped message
    if (this.refs.insuranceSkippedMessage) {
      this.refs.insuranceSkippedMessage.style.display = 'block';
    }
    
    // Show warehouse section
    if (this.refs.warehouseSection) {
      this.refs.warehouseSection.style.display = 'block';
    }
    
    // Store warehouse mode in session storage
    sessionStorage.setItem('warehouseMode', 'true');
    
    // Dispatch warehouse state changed event
    document.dispatchEvent(new CustomEvent('warehouseStateChanged', {
      detail: {
        warehouseVisible: true,
        warehouseValidated: this.#warehouseValidated,
        warehouseCheckRequired: true,
        action: 'warehouse_section_shown'
      }
    }));
    
    // Update skip link text
    if (this.refs.skipInsuranceLink) {
      this.refs.skipInsuranceLink.textContent = this.dataset.skipInsuranceGoBackText || 'Go back';
    }
    
    // Update hidden field
    this.#updateFormInjectedInput('_insuranceSkipped', 'true');
    
    // Clear insurance data properties now that insurance is skipped
    this.#updateInsuranceHiddenFields();
    
    // Store in session storage
    sessionStorage.setItem('insuranceSkipped', 'true');
    
    // Remove required attribute from insurance fields
    this.#setInsuranceFieldsRequired(false);
    
    // Clear any existing warehouse input
    const warehouseInput = this.#getInput('warehouseInput');
    if (warehouseInput) {
      warehouseInput.value = '';
    }
    
    // Clear notification area
    this.refs.notificationArea.innerHTML = '';
    this.refs.notificationArea.style.display = 'none';
    
    // Update global state
    window.warehouseCheckRequired = true;
    this.#updateAddToCartButtonState();
    
    // Dispatch custom event for integration
    document.dispatchEvent(new CustomEvent('insuranceSkipped', { 
      detail: { skipped: true }
    }));
  }

  /**
   * Restart insurance form
   */
  #restartInsurance() {
    this.#insuranceSkipped = false;
    
    // Show insurance form fields
    if (this.refs.insuranceFormFields) {
      this.refs.insuranceFormFields.classList.remove('hidden');
    }
    
    // Hide skipped message
    if (this.refs.insuranceSkippedMessage) {
      this.refs.insuranceSkippedMessage.style.display = 'none';
    }
    
    // Hide warehouse section if not in warehouse_only mode
    if (this.#displayMode !== 'warehouse_only' && this.refs.warehouseSection) {
      this.refs.warehouseSection.style.display = 'none';
    }
    
    // Clear warehouse mode from session storage if not in warehouse_only mode
    if (this.#displayMode !== 'warehouse_only') {
      sessionStorage.setItem('warehouseMode', 'false');
    }
    
    // Dispatch warehouse state changed event
    document.dispatchEvent(new CustomEvent('warehouseStateChanged', {
      detail: {
        warehouseVisible: this.#displayMode === 'warehouse_only',
        warehouseValidated: this.#warehouseValidated,
        warehouseCheckRequired: false,
        action: 'warehouse_section_hidden'
      }
    }));
    
    // Update skip link text
    if (this.refs.skipInsuranceLink) {
      this.refs.skipInsuranceLink.textContent = this.dataset.skipInsuranceLinkText || 'Click here';
    }
    
    // Update hidden field
    this.#updateFormInjectedInput('_insuranceSkipped', 'false');
    
    // Restore insurance data properties now that insurance is not skipped
    this.#updateInsuranceHiddenFields();
    
    // Update session storage
    sessionStorage.setItem('insuranceSkipped', 'false');
    
    // Clear warehouse field and notification
    const warehouseInput = this.#getInput('warehouseInput');
    if (warehouseInput) {
      warehouseInput.value = '';
    }
    
    this.refs.notificationArea.innerHTML = '';
    this.refs.notificationArea.style.display = 'none';
    
    // Restore required attribute to insurance fields
    this.#setInsuranceFieldsRequired(true);
    
    // Reset validation states
    this.#warehouseValidated = false;
    
    // Update global state
    window.warehouseCheckRequired = false;
    this.#updateAddToCartButtonState();
    
    // Dispatch custom event for integration
    document.dispatchEvent(new CustomEvent('insuranceSkipped', { 
      detail: { skipped: false }
    }));
  }

  /**
   * Set required attribute on insurance fields
   * @param {boolean} required - Whether fields should be required
   */
  #setInsuranceFieldsRequired(required) {
    ['insuranceIdInput', 'firstNameInput', 'lastNameInput', 'dobDayInput', 'dobYearInput'].forEach(refName => {
      const input = this.#getInput(refName);
      if (input) {
        if (required) {
          input.setAttribute('required', '');
        } else {
          input.removeAttribute('required');
        }
      }
    });

    if (this.refs.dobMonth) {
      if (required) {
        this.refs.dobMonth.setAttribute('required', '');
      } else {
        this.refs.dobMonth.removeAttribute('required');
      }
    }
  }

  /**
   * Handle insurance form submission
   */
  async #handleInsuranceSubmit() {
    // Validate insurance fields
    if (!this.#validateInsuranceFields()) {
      return;
    }
    
    // Clear previous results
    this.#clearPreviousResults();
    
    // Show loading state
    this.#setInsuranceLoadingState(true);
    
    try {
      const payload = this.#buildInsurancePayload();
      const response = await this.#makeInsuranceApiCall(payload);
      this.#handleInsuranceApiResponse(response);
    } catch (error) {
      this.#handleInsuranceApiError(error);
    } finally {
      this.#setInsuranceLoadingState(false);
    }
  }

  /**
   * Handle warehouse form submission
   */
  #handleWarehouseSubmit() {
    const warehouseInput = this.#getInput('warehouseInput');
    
    // Validate warehouse field
    if (!warehouseInput || !warehouseInput.checkValidity()) {
      if (warehouseInput) warehouseInput.reportValidity();
      return;
    }
    
    const warehouseNumber = warehouseInput.value.trim();
    
    if (!warehouseNumber) {
      this.#displayNotification({
        type: 'error',
        title: 'Warehouse Number Required',
        content: 'Please enter your warehouse number.'
      });
      warehouseInput.focus();
      warehouseInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    
    // Show loading state
    this.#setWarehouseLoadingState(true);
    
    // Validate warehouse number
    const isValid = this.#validWarehouses.includes(warehouseNumber);
    
    if (isValid) {
      this.#warehouseValidated = true;
      window.warehouseCheckRequired = false;
      
      // Dispatch warehouse state changed event
      document.dispatchEvent(new CustomEvent('warehouseStateChanged', {
        detail: {
          warehouseVisible: true,
          warehouseValidated: true,
          warehouseCheckRequired: false,
          action: 'warehouse_validation_success'
        }
      }));
      
      this.#displayNotification({
        type: 'success',
        title: this.dataset.warehouseSuccessTitle || 'Great!',
        content: this.dataset.warehouseSuccessMessage || 'Please proceed...'
      });
    } else {
      this.#warehouseValidated = false;
      
      // Dispatch warehouse state changed event
      document.dispatchEvent(new CustomEvent('warehouseStateChanged', {
        detail: {
          warehouseVisible: true,
          warehouseValidated: false,
          warehouseCheckRequired: true,
          action: 'warehouse_validation_failure'
        }
      }));
      
      const errorMessage = this.dataset.warehouseErrorMessage || 
        'If you are on the mainland, please <a href="#" class="employee-link">go back</a> and enter your Aetna insurance information.';
      
      this.#displayNotification({
        type: 'warning',
        title: this.dataset.warehouseErrorTitle || 'Sorry, We Do Not Recognize That Warehouse',
        content: errorMessage
      });
      
      // Attach employee link functionality
      setTimeout(() => this.#attachEmployeeLinks(), 100);
    }
    
    // Update add to cart button state
    this.#updateAddToCartButtonState();
    
    // Update session storage
    this.#updateWarehouseInSession();
    
    // Re-enable the button
    this.#setWarehouseLoadingState(false);
  }


  /**
   * Build insurance API payload
   * @returns {Object} The API payload
   */
  #buildInsurancePayload() {
    const insuranceIdInput = this.#getInput('insuranceIdInput');
    const firstNameInput = this.#getInput('firstNameInput');
    const lastNameInput = this.#getInput('lastNameInput');
    const dobDayInput = this.#getInput('dobDayInput');
    const dobYearInput = this.#getInput('dobYearInput');

    // Construct DOB
    const dobValue = `${dobYearInput.value || ''}-${this.refs.dobMonth.value || ''}-${dobDayInput.value ? dobDayInput.value.padStart(2, '0') : ''}`;
    
    return {
      insuranceId: insuranceIdInput.value,
      firstName: firstNameInput.value,
      lastName: lastNameInput.value,
      dob: dobValue
    };
  }

  /**
   * Make API call for insurance eligibility checking
   * @param {Object} payload - The API payload
   * @returns {Promise<Object>} The API response
   */
  async #makeInsuranceApiCall(payload) {
    const timeout = parseInt(this.dataset.apiTimeout) * 1000 || 10000;
    const controller = new AbortController();
    
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    try {
      const response = await fetch(this.#insuranceApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  /**
   * Handle insurance API response
   * @param {Object} data - The API response data
   */
  #handleInsuranceApiResponse(data) {
    
    // Store form data in session storage
    this.#updateInsuranceInSession();
    
    if (data.eligibilityResponse && data.eligibilityResponse.amount) {
      this.#insuranceValidated = true;
      
      const successMessage = this.dataset.insuranceSuccessMessage || 'We estimate that [amount] will be covered.';
      const message = successMessage.replace('[amount]', data.eligibilityResponse.amount);
      
      this.#displayNotification({
        type: 'success',
        title: this.dataset.insuranceSuccessTitle || 'Great News!',
        content: message
      });
    } else {
      this.#insuranceValidated = false;
      
      this.#displayNotification({
        type: 'warning',
        title: this.dataset.insuranceErrorTitle || 'Eligibility Check Error',
        content: 'Eligibility check completed, but no coverage amount was found.'
      });
    }
    
    this.#updateAddToCartButtonState();
  }

  /**
   * Handle insurance API error
   * @param {Error} error - The error that occurred
   */
  #handleInsuranceApiError(error) {
    console.error('Insurance API Error:', error);
    
    let errorMessage = this.dataset.insuranceErrorMessage || 'An error occurred while checking eligibility. Please try again.';
    
    if (error.name === 'AbortError') {
      errorMessage = 'Request timed out. Please try again.';
    } else if (error.message.includes('fetch')) {
      errorMessage = 'Network error. Please check your connection and try again.';
    }
    
    this.#displayNotification({
      type: 'error',
      title: this.dataset.insuranceErrorTitle || 'Eligibility Check Error',
      content: errorMessage
    });
  }

  /**
   * Clear previous results
   */
  #clearPreviousResults() {
    this.#insuranceValidated = false;
    this.#warehouseValidated = false;
    window.availityWarehouseValidated = false;
    
    // Clear notification
    this.refs.notificationArea.innerHTML = '';
    this.refs.notificationArea.style.display = 'none';
    
    // Update add to cart button state
    this.#updateAddToCartButtonState();
  }

  /**
   * Set insurance loading state
   * @param {boolean} isLoading - Whether to show loading state
   */
  #setInsuranceLoadingState(isLoading) {
    if (!this.refs.insuranceSubmitButton) return;
    
    this.refs.insuranceSubmitButton.disabled = isLoading;
    
    if (isLoading) {
      if (this.refs.insuranceSubmitButtonText) this.refs.insuranceSubmitButtonText.style.opacity = '0';
      if (this.refs.insuranceSubmitButtonSpinner) this.refs.insuranceSubmitButtonSpinner.style.display = 'block';
    } else {
      if (this.refs.insuranceSubmitButtonText) this.refs.insuranceSubmitButtonText.style.opacity = '1';
      if (this.refs.insuranceSubmitButtonSpinner) this.refs.insuranceSubmitButtonSpinner.style.display = 'none';
    }
  }

  /**
   * Set warehouse loading state
   * @param {boolean} isLoading - Whether to show loading state
   */
  #setWarehouseLoadingState(isLoading) {
    if (!this.refs.warehouseSubmitButton) return;
    
    this.refs.warehouseSubmitButton.disabled = isLoading;
    
    if (isLoading) {
      if (this.refs.warehouseSubmitButtonText) this.refs.warehouseSubmitButtonText.style.opacity = '0';
      if (this.refs.warehouseSubmitButtonSpinner) this.refs.warehouseSubmitButtonSpinner.style.display = 'block';
    } else {
      if (this.refs.warehouseSubmitButtonText) this.refs.warehouseSubmitButtonText.style.opacity = '1';
      if (this.refs.warehouseSubmitButtonSpinner) this.refs.warehouseSubmitButtonSpinner.style.display = 'none';
    }
  }

  /**
   * Update insurance fields in session storage
   */
  #updateInsuranceInSession() {
    if (this.dataset.sessionPersistence !== 'true') return;
    
    const insuranceIdInput = this.#getInput('insuranceIdInput');
    const firstNameInput = this.#getInput('firstNameInput');
    const lastNameInput = this.#getInput('lastNameInput');
    const dobDayInput = this.#getInput('dobDayInput');
    const dobYearInput = this.#getInput('dobYearInput');

    if (!insuranceIdInput) return;

    const dobValue = `${dobYearInput?.value || ''}-${this.refs.dobMonth?.value || ''}-${dobDayInput?.value ? dobDayInput.value.padStart(2, '0') : ''}`;
    
    const formData = {
      insuranceId: insuranceIdInput.value,
      firstName: firstNameInput?.value || '',
      lastName: lastNameInput?.value || '',
      dob: dobValue
    };
    
    sessionStorage.setItem('eligibilityFormData', JSON.stringify(formData));
  }

  /**
   * Update warehouse field in session storage
   */
  #updateWarehouseInSession() {
    if (this.dataset.sessionPersistence !== 'true') return;
    
    const warehouseInput = this.#getInput('warehouseInput');
    if (!warehouseInput) return;
    
    const warehouseNumber = warehouseInput.value;
    sessionStorage.setItem('pdpWarehouseChecker', JSON.stringify({ warehouse: warehouseNumber }));
  }

  /**
   * Update insurance hidden fields
   */
  #updateInsuranceHiddenFields() {
    if (this.#displayMode === 'warehouse_only') return;

    const insuranceIdInput = this.#getInput('insuranceIdInput');
    const firstNameInput = this.#getInput('firstNameInput');
    const lastNameInput = this.#getInput('lastNameInput');
    const dobDayInput = this.#getInput('dobDayInput');
    const dobYearInput = this.#getInput('dobYearInput');

    // Only populate insurance data properties if insurance is not skipped
    if (!this.#insuranceSkipped) {
      if (insuranceIdInput) {
        this.#updateFormInjectedInput('Insurance ID', insuranceIdInput.value);
      }
      if (firstNameInput) {
        this.#updateFormInjectedInput('First Name', firstNameInput.value);
      }
      if (lastNameInput) {
        this.#updateFormInjectedInput('Last Name', lastNameInput.value);
      }
      
      const dobValue = `${dobYearInput?.value || ''}-${this.refs.dobMonth?.value || ''}-${dobDayInput?.value ? dobDayInput.value.padStart(2, '0') : ''}`;
      this.#updateFormInjectedInput('Date of Birth', dobValue);
    } else {
      // Clear insurance data properties when skipped (but preserve form inputs)
      this.#updateFormInjectedInput('Insurance ID', '');
      this.#updateFormInjectedInput('First Name', '');
      this.#updateFormInjectedInput('Last Name', '');
      this.#updateFormInjectedInput('Date of Birth', '');
    }
  }

  /**
   * Update warehouse hidden fields
   */
  #updateWarehouseHiddenFields() {
    if (this.#displayMode === 'insurance_only') return;

    const warehouseInput = this.#getInput('warehouseInput');
    if (warehouseInput) {
      this.#updateFormInjectedInput('_warehouseNumberUsed', warehouseInput.value);
    }
  }

  /**
   * Update add to cart button state
   */
  #updateAddToCartButtonState() {
    // Check if add-to-cart disabling is enabled
    const disableAddToCart = this.dataset.disableAddToCart === 'true';
    
    // Enhanced debug detection
    const urlParams = new URLSearchParams(window.location.search);
    const urlDebug = urlParams.get('debug') === 'true' || urlParams.get('debug') === 'availity';
    const globalDebug = window.AVAILITY_DEBUG === true;
    const shopifyDesignMode = !!(window.Shopify && window.Shopify.designMode);
    const DEBUG = shopifyDesignMode || urlDebug || globalDebug;
    if (DEBUG) console.log('[AVAILITY DEBUG] updateAddToCartButtonState called', {
      disableAddToCart,
      globalStates: {
        scanReceived: window.scanReceived,
        isMeasurementReady: window.isMeasurementReady,
        warehouseCheckRequired: window.warehouseCheckRequired
      }
    });
    
    if (!disableAddToCart) {
      if (DEBUG) console.log('[AVAILITY DEBUG] Disabling not enabled, exiting early');
      return; // Exit early if disabling is not enabled
    }
    
    
    // Check if measurement checker exists and has completed validation
    const measurementCheckerExists = document.querySelector('measurement-checker-component');
    const measurementReady = measurementCheckerExists && (window.isMeasurementReady || window.scanReceived);
    
    if (DEBUG) console.log('[AVAILITY DEBUG] #updateAddToCartButtonState() - Measurement state check:', {
      measurementCheckerExists: !!measurementCheckerExists,
      windowIsMeasurementReady: window.isMeasurementReady,
      windowScanReceived: window.scanReceived,
      measurementReady,
      buttonLogicFormula: 'measurementCheckerExists && (window.isMeasurementReady || window.scanReceived)'
    });
    
    // Determine if validation is complete based on display mode.
    // When eligibility check is disabled, the API call is skipped but form
    // fields and consent are still required before add-to-cart is enabled.
    let validationComplete = false;

    switch (this.#displayMode) {
      case 'insurance_only':
        // Whether eligibility API is called or not, fields must be filled
        validationComplete = this.#insuranceValidated || this.#areInsuranceFieldsFilled();
        break;
      case 'warehouse_only':
        validationComplete = this.#warehouseValidated;
        break;
      case 'full':
      default:
        if (this.#insuranceSkipped) {
          // If insurance is skipped, MUST have warehouse validation
          validationComplete = this.#warehouseValidated;
        } else {
          // If insurance is not skipped, check if insurance is complete
          validationComplete = this.#insuranceValidated || this.#areInsuranceFieldsFilled();
        }

        // Also require consent if validation is otherwise complete
        if (validationComplete) {
          validationComplete = this.#isConsentGiven();
        }
        break;
    }
    
    // If measurement checker exists, require both validations to be complete
    if (measurementCheckerExists) {
      validationComplete = validationComplete && measurementReady;
    }
    
    // Intercept product form submissions globally
    this.#interceptProductFormSubmissions();
    
    // Find all add-to-cart components on the page
    const addToCartComponents = document.querySelectorAll('add-to-cart-component');
    
    addToCartComponents.forEach(component => {
      const button = component.querySelector('[ref="addToCartButton"]');
      if (!button) return;
      
      // Store the original disabled state if we haven't already
      if (!button.hasAttribute('data-original-disabled')) {
        button.setAttribute('data-original-disabled', button.disabled.toString());
      }
      
      // Get the original disabled state (from inventory, variant availability, etc.)
      const originalDisabled = button.getAttribute('data-original-disabled') === 'true';
      
      // Only enable if validation is complete AND the button wasn't originally disabled
      if (validationComplete && !originalDisabled) {
        button.disabled = false;
        button.removeAttribute('data-availity-disabled');
        this.#hidePopover(component);
      } else if (!validationComplete) {
        button.disabled = true;
        button.setAttribute('data-availity-disabled', 'true');
        this.#showPopover(component);
      }
    });
    
    // Update global state for compatibility
    window.availityInsuranceValidated = this.#insuranceValidated;
    window.availityWarehouseValidated = this.#warehouseValidated;
    window.warehouseCheckRequired = this.#insuranceSkipped;
  }

  /**
   * Intercept ProductFormComponent submit methods and AddToCartComponent animations
   */
  #interceptProductFormSubmissions() {
    if (window.availityProductFormIntercepted) return;
    
    window.availityProductFormIntercepted = true;
    
    // Intercept ProductFormComponent submissions
    const productForms = document.querySelectorAll('product-form-component');
    productForms.forEach(productForm => {
      if (productForm.availityIntercepted) return;
      productForm.availityIntercepted = true;
      
      // Store the original handleSubmit method
      const originalHandleSubmit = productForm.handleSubmit.bind(productForm);
      
      // Override with our validation wrapper
      productForm.handleSubmit = (event) => {
        // Check if add-to-cart validation is enabled
        const disableAddToCart = this.dataset.disableAddToCart === 'true';
        if (!disableAddToCart) {
          return originalHandleSubmit(event);
        }
        
        // Check measurement checker if it exists
        const measurementCheckerExists = document.querySelector('measurement-checker-component');
        if (measurementCheckerExists) {
          const measurementReady = window.isMeasurementReady || window.scanReceived;
          if (!measurementReady) {
            event.preventDefault();
            return;
          }
        }
        
        // Run availity validation
        if (!this.#validateInsuranceFields()) {
          event.preventDefault();
          return;
        }
        
        return originalHandleSubmit(event);
      };
    });
    
    // Intercept AddToCartComponent animations
    const addToCartComponents = document.querySelectorAll('add-to-cart-component');
    addToCartComponents.forEach(component => {
      if (component.availityAnimationIntercepted) return;
      component.availityAnimationIntercepted = true;
      
      // Store the original handleClick method
      const originalHandleClick = component.handleClick?.bind(component);
      if (!originalHandleClick) return;
      
      // Override with our validation wrapper
      component.handleClick = (event) => {
        // Check if add-to-cart validation is enabled
        const disableAddToCart = this.dataset.disableAddToCart === 'true';
        if (!disableAddToCart) {
          return originalHandleClick(event);
        }
        
        // Check measurement checker if it exists
        const measurementCheckerExists = document.querySelector('measurement-checker-component');
        if (measurementCheckerExists) {
          const measurementReady = window.isMeasurementReady || window.scanReceived;
          if (!measurementReady) {
            // Don't run animation if validation fails
            return;
          }
        }
        
        // Run availity validation
        if (!this.#validateInsuranceFields()) {
          // Don't run animation if validation fails
          return;
        }
        
        return originalHandleClick(event);
      };
    });
  }

  /**
   * Show popover for disabled add-to-cart button
   * @param {HTMLElement} component - The add-to-cart component
   */
  #showPopover(component) {
    // Enhanced debug detection
    const urlParams = new URLSearchParams(window.location.search);
    const urlDebug = urlParams.get('debug') === 'true' || urlParams.get('debug') === 'availity';
    const globalDebug = window.AVAILITY_DEBUG === true;
    const shopifyDesignMode = !!(window.Shopify && window.Shopify.designMode);
    const DEBUG = shopifyDesignMode || urlDebug || globalDebug;
    
    if (DEBUG) console.log('[AVAILITY DEBUG] #showPopover() called');
    
    let popover = component.querySelector('.availity-disabled-popover');
    
    if (!popover) {
      // Create popover element
      popover = document.createElement('div');
      popover.className = 'availity-disabled-popover';
      
      // Add popover to component
      component.appendChild(popover);
      if (DEBUG) console.log('[AVAILITY DEBUG] #showPopover() - Created new popover element');
    }
    
    // Get missing requirements as a list
    const missingRequirements = this.#getMissingRequirements();
    
    if (DEBUG) console.log('[AVAILITY DEBUG] #showPopover() - Missing requirements from getMissingRequirements():', missingRequirements);
    
    // Build dynamic popover content
    let popoverHTML = '';
    if (missingRequirements.length > 0) {
      const baseMessage = this.dataset.disabledPopoverMessage || 'Please complete the following first:';
      popoverHTML = `<div class="popover-message">${baseMessage}</div>`;
      popoverHTML += '<ul class="popover-requirements">';
      missingRequirements.forEach(requirement => {
        popoverHTML += `<li>${requirement}</li>`;
      });
      popoverHTML += '</ul>';
      
      if (DEBUG) console.log('[AVAILITY DEBUG] #showPopover() - Built dynamic popover content:', {
        baseMessage,
        requirements: missingRequirements,
        popoverHTML
      });
    } else {
      // Fallback to static message
      popoverHTML = this.dataset.disabledPopoverMessage || 'Complete required steps first';
      if (DEBUG) console.log('[AVAILITY DEBUG] #showPopover() - Using fallback static message:', popoverHTML);
    }
    
    // Update popover content
    popover.innerHTML = popoverHTML;
    
    if (DEBUG) console.log('[AVAILITY DEBUG] #showPopover() - Final popover HTML set:', popover.innerHTML);
    
    // Show popover
    popover.classList.add('show');
    
    if (DEBUG) console.log('[AVAILITY DEBUG] #showPopover() - Popover shown with "show" class added');
  }

  /**
   * Get list of missing requirements for validation
   * @returns {string[]} Array of missing requirement descriptions
   */
  #getMissingRequirements() {
    const requirements = [];
    
    // Check measurement checker if it exists
    const measurementCheckerExists = document.querySelector('measurement-checker-component');
    if (measurementCheckerExists) {
      const measurementReady = window.isMeasurementReady || window.scanReceived;
      
      if (!measurementReady) {
        // Use custom text from settings, or fall back to default
        const measurementText = this.dataset.popoverMeasurementText || 'Connecting your foot scan';
        requirements.push(measurementText);
      }
    }
    
    // Check availity/warehouse requirements
    let availityComplete = false;
    if (this.#insuranceSkipped) {
      // If insurance is skipped, only warehouse validation matters
      availityComplete = this.#warehouseValidated;
      if (!availityComplete) {
        const warehouseText = this.dataset.popoverWarehouseText || 'Warehouse check';
        requirements.push(warehouseText);
      }
    } else {
      // If insurance is not skipped, check if validated OR fields are filled
      availityComplete = this.#insuranceValidated || this.#areInsuranceFieldsFilled();
      if (!availityComplete) {
        const insuranceText = this.dataset.popoverInsuranceText || 'Insurance form';
        requirements.push(insuranceText);
      }
    }
    
    // Check consent requirements
    const consentValid = this.#isConsentGiven();
    if (!consentValid) {
      const consentText = this.dataset.popoverConsentText || 'Terms and conditions consent';
      requirements.push(consentText);
    }
    
    return requirements;
  }

  /**
   * Hide popover for enabled add-to-cart button
   * @param {HTMLElement} component - The add-to-cart component
   */
  #hidePopover(component) {
    const popover = component.querySelector('.availity-disabled-popover');
    if (popover) {
      popover.classList.remove('show');
    }
  }

  /**
   * Attach employee link functionality
   */
  #attachEmployeeLinks() {
    const employeeLinks = this.refs.notificationArea.querySelectorAll('.employee-link');
    
    employeeLinks.forEach(link => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        if (this.refs.skipInsuranceLink) {
          this.refs.skipInsuranceLink.click();
        }
      });
    });
  }

  /**
   * Display notification
   * @param {Object} config - The notification configuration
   * @param {string} config.type - The notification type ('success', 'warning', 'error')
   * @param {string} config.title - The notification title
   * @param {string|string[]} config.content - The notification content
   */
  #displayNotification(config) {
    const { notificationArea } = this.refs;
    
    // Clear previous content
    notificationArea.innerHTML = '';
    
    // Create title
    if (config.title) {
      const titleElement = document.createElement('div');
      titleElement.className = 'availity-checker__notification-title';
      titleElement.textContent = config.title;
      notificationArea.appendChild(titleElement);
    }
    
    // Add divider if there's both title and content
    if (config.title && config.content) {
      const divider = document.createElement('div');
      divider.className = 'availity-checker__notification-divider';
      notificationArea.appendChild(divider);
    }
    
    // Create content
    if (config.content) {
      const contentElement = document.createElement('div');
      contentElement.className = 'availity-checker__notification-content';
      
      if (Array.isArray(config.content)) {
        config.content.forEach(paragraph => {
          const p = document.createElement('p');
          p.innerHTML = paragraph; // Use innerHTML to support HTML content like links
          contentElement.appendChild(p);
        });
      } else {
        const p = document.createElement('p');
        p.innerHTML = config.content; // Use innerHTML to support HTML content like links
        contentElement.appendChild(p);
      }
      
      notificationArea.appendChild(contentElement);
    }
    
    // Apply styling based on type
    notificationArea.className = `availity-checker__notification availity-checker__notification--${config.type}`;
    notificationArea.style.display = 'block';
    
    // Apply font size settings from the component
    const titleFontSize = this.dataset.notificationTitleFontSize || '14';
    const contentFontSize = this.dataset.notificationContentFontSize || '14';
    notificationArea.style.setProperty('--notification-title-font-size', `${titleFontSize}px`);
    notificationArea.style.setProperty('--notification-content-font-size', `${contentFontSize}px`);
  }

  /**
   * Set up global functions for compatibility with existing code
   */
  #setupGlobalFunctions() {
    // Don't override the global checkEnableAddToCart if measurement checker exists
    // The measurement checker will handle coordinating both components
    const measurementCheckerExists = document.querySelector('measurement-checker-component');
    if (!measurementCheckerExists) {
      // Only set up global function if measurement checker doesn't exist
      window.checkEnableAddToCart = this.#checkEnableAddToCart.bind(this);
    }
    
    // Global function to display notifications (for compatibility)
    if (!window.displayNotification) {
      window.displayNotification = (config, elementId, type) => {
        // If elementId is provided and it's not our notification area, fall back to basic display
        if (elementId && elementId !== 'availity-notification') {
          const element = document.getElementById(elementId);
          if (element) {
            element.innerHTML = typeof config === 'string' ? config : config.content;
            element.style.display = 'block';
            return;
          }
        }
        
        // Use our notification system
        if (typeof config === 'string') {
          this.#displayNotification({
            type: type || 'success',
            title: '',
            content: config
          });
        } else {
          this.#displayNotification({
            type: type || 'success',
            title: config.title || '',
            content: config.content || config
          });
        }
      };
    }
  }

  /**
   * Set up variant update listeners to integrate with theme's existing logic
   */
  #setupVariantListeners() {
    const { signal } = this.#abortController;
    
    // Listen for variant updates from the theme
    const target = this.closest('.shopify-section') || document;
    target.addEventListener('variant:update', this.#handleVariantUpdate.bind(this), { signal });
    target.addEventListener('variant:selected', this.#handleVariantUpdate.bind(this), { signal });
    
    // Also listen for cart events to ensure proper state management
    target.addEventListener('cart:update', this.#handleCartAdd.bind(this), { signal });
  }

  /**
   * Handle variant update events from the theme
   */
  #handleVariantUpdate(event) {
    // Small delay to ensure the theme has updated its button states first
    setTimeout(() => {
      // Update the original disabled state based on variant availability
      const addToCartComponents = document.querySelectorAll('add-to-cart-component');
      addToCartComponents.forEach(component => {
        const button = component.querySelector('[ref="addToCartButton"]');
        if (button) {
          // Update the original disabled state based on current variant availability
          const isVariantAvailable = event.detail?.resource?.available !== false;
          button.setAttribute('data-original-disabled', (!isVariantAvailable).toString());
        }
      });
      
      // Now update the availity-based state
      this.#updateAddToCartButtonState();
    }, 10);
  }

  /**
   * Handle cart add events
   */
  #handleCartAdd() {
    // Ensure validation is maintained after cart operations
    this.#updateAddToCartButtonState();
  }

  /**
   * Check if insurance fields are filled out (not necessarily valid)
   * @returns {boolean} True if all required insurance fields have some content
   */
  #areInsuranceFieldsFilled() {
    if (this.#insuranceSkipped) {
      return true; // If insurance is skipped, consider it "filled"
    }

    const insuranceIdInput = this.#getInput('insuranceIdInput');
    const firstNameInput = this.#getInput('firstNameInput');
    const lastNameInput = this.#getInput('lastNameInput');
    const dobDayInput = this.#getInput('dobDayInput');
    const dobYearInput = this.#getInput('dobYearInput');

    if (!insuranceIdInput || !firstNameInput || !lastNameInput || !dobDayInput || !dobYearInput || !this.refs.dobMonth) {
      return false;
    }

    // Check if all fields have some content (not necessarily valid)
    const insuranceId = insuranceIdInput.value?.trim();
    const firstName = firstNameInput.value?.trim();
    const lastName = lastNameInput.value?.trim();
    const dobDay = dobDayInput.value?.trim();
    const dobYear = dobYearInput.value?.trim();
    const dobMonth = this.refs.dobMonth.value?.trim();

    // Just check if all fields have some content
    return insuranceId && firstName && lastName && dobDay && dobYear && dobMonth;
  }

  /**
   * Check if insurance fields are properly filled and valid
   * @returns {boolean} True if all required insurance fields are valid
   */
  #areInsuranceFieldsProperlyFilled() {
    if (this.#insuranceSkipped) {
      return true; // If insurance is skipped, consider it properly filled
    }

    const insuranceIdInput = this.#getInput('insuranceIdInput');
    const firstNameInput = this.#getInput('firstNameInput');
    const lastNameInput = this.#getInput('lastNameInput');
    const dobDayInput = this.#getInput('dobDayInput');
    const dobYearInput = this.#getInput('dobYearInput');

    if (!insuranceIdInput || !firstNameInput || !lastNameInput || !dobDayInput || !dobYearInput || !this.refs.dobMonth) {
      return false;
    }

    // Check if all fields have valid values
    const insuranceId = insuranceIdInput.value?.trim();
    const firstName = firstNameInput.value?.trim();
    const lastName = lastNameInput.value?.trim();
    const dobDay = dobDayInput.value?.trim();
    const dobYear = dobYearInput.value?.trim();
    const dobMonth = this.refs.dobMonth.value?.trim();

    // Basic validation - all fields must be filled
    if (!insuranceId || !firstName || !lastName || !dobDay || !dobYear || !dobMonth) {
      return false;
    }

    // Basic format validation
    const insuranceIdPattern = /^([wW]\d{9}|\d{9}[wW])$/;
    const dayPattern = /^(0?[1-9]|[12][0-9]|3[01])$/;
    const yearPattern = /^\d{4}$/;

    return insuranceIdPattern.test(insuranceId) && 
           dayPattern.test(dobDay) && 
           yearPattern.test(dobYear) &&
           firstName.length > 0 && 
           lastName.length > 0;
  }

  /**
   * Validate insurance fields and highlight any errors
   * @returns {boolean} True if all fields are valid
   */
  #validateInsuranceFields() {
    // First check deferred payment consent if required
    if (!this.#validateDeferredPaymentConsent()) {
      return false;
    }

    // If insurance is skipped, we need to check if warehouse validation is complete
    if (this.#insuranceSkipped) {
      return this.#warehouseValidated; // Must have completed warehouse validation
    }

    let isValid = true;
    const errors = [];

    const insuranceIdInput = this.#getInput('insuranceIdInput');
    const firstNameInput = this.#getInput('firstNameInput');
    const lastNameInput = this.#getInput('lastNameInput');
    const dobDayInput = this.#getInput('dobDayInput');
    const dobYearInput = this.#getInput('dobYearInput');

    if (!insuranceIdInput || !firstNameInput || !lastNameInput || !dobDayInput || !dobYearInput || !this.refs.dobMonth) {
      return false;
    }

    // Get field values
    const insuranceId = insuranceIdInput.value?.trim();
    const firstName = firstNameInput.value?.trim();
    const lastName = lastNameInput.value?.trim();
    const dobDay = dobDayInput.value?.trim();
    const dobYear = dobYearInput.value?.trim();
    const dobMonth = this.refs.dobMonth.value?.trim();

    // Clear existing error states
    this.#clearFieldErrors();

    // Validate Insurance ID
    const insuranceIdPattern = /^([wW]\d{9}|\d{9}[wW])$/;
    if (!insuranceId) {
      this.#showFieldError(insuranceIdInput, 'Insurance ID is required');
      isValid = false;
    } else if (!insuranceIdPattern.test(insuranceId)) {
      this.#showFieldError(insuranceIdInput, 'Insurance ID must have a W at the start or end with 9 digits (e.g., W123456789)');
      isValid = false;
    }

    // Validate First Name
    if (!firstName) {
      this.#showFieldError(firstNameInput, 'First name is required');
      isValid = false;
    }

    // Validate Last Name
    if (!lastName) {
      this.#showFieldError(lastNameInput, 'Last name is required');
      isValid = false;
    }

    // Validate DOB Month
    if (!dobMonth) {
      this.#showFieldError(this.refs.dobMonth, 'Month is required');
      isValid = false;
    }

    // Validate DOB Day
    const dayPattern = /^(0?[1-9]|[12][0-9]|3[01])$/;
    if (!dobDay) {
      this.#showFieldError(dobDayInput, 'Day is required');
      isValid = false;
    } else if (!dayPattern.test(dobDay)) {
      this.#showFieldError(dobDayInput, 'Invalid day (1-31)');
      isValid = false;
    }

    // Validate DOB Year
    const yearPattern = /^\d{4}$/;
    if (!dobYear) {
      this.#showFieldError(dobYearInput, 'Year is required');
      isValid = false;
    } else if (!yearPattern.test(dobYear)) {
      this.#showFieldError(dobYearInput, 'Invalid year (4 digits)');
      isValid = false;
    }

    // Focus first error field if validation failed
    if (!isValid) {
      this.#focusFirstErrorField();
    }

    return isValid;
  }

  /**
   * Show error state for a field
   * @param {HTMLElement} field - The field element
   * @param {string} message - Error message
   */
  #showFieldError(field, message) {
    // Find the wrapper element (could be animated-input-field or select wrapper)
    let wrapper = field.closest('.animated-input-field') || field.closest('.availity-checker__select-wrapper') || field.parentElement;
    
    if (wrapper) {
      wrapper.classList.add('availity-checker__field--error');
      
      // Add error message if it doesn't exist
      let errorMessage = wrapper.querySelector('.availity-checker__field-error');
      if (!errorMessage) {
        errorMessage = document.createElement('div');
        errorMessage.className = 'availity-checker__field-error';
        wrapper.appendChild(errorMessage);
      }
      errorMessage.textContent = message;
    }
  }

  /**
   * Clear all field error states
   */
  #clearFieldErrors() {
    const errorFields = document.querySelectorAll('.availity-checker__field--error');
    errorFields.forEach(field => {
      field.classList.remove('availity-checker__field--error');
      const errorMessage = field.querySelector('.availity-checker__field-error');
      if (errorMessage) {
        errorMessage.remove();
      }
    });
  }

  /**
   * Focus the first field with an error
   */
  #focusFirstErrorField() {
    const firstError = this.querySelector('.availity-checker__field--error input, .availity-checker__field--error select');
    if (firstError) {
      firstError.focus();
      firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  /**
   * Check if add to cart should be enabled (for compatibility)
   */
  #checkEnableAddToCart() {
    // Enhanced debug detection
    const urlParams = new URLSearchParams(window.location.search);
    const urlDebug = urlParams.get('debug') === 'true' || urlParams.get('debug') === 'availity';
    const globalDebug = window.AVAILITY_DEBUG === true;
    const shopifyDesignMode = !!(window.Shopify && window.Shopify.designMode);
    const DEBUG = shopifyDesignMode || urlDebug || globalDebug;
    
    if (DEBUG) console.log('[AVAILITY DEBUG] checkEnableAddToCart called from AVAILITY component');
    this.#updateAddToCartButtonState();
  }

  /**
   * Public method to update button state (called by global function)
   */
  updateButtonState() {
    this.#updateAddToCartButtonState();
  }

  /**
   * Set up event-driven measurement state monitoring
   */
  #startMeasurementStateMonitoring() {
    const { signal } = this.#abortController;
    
    // Listen for measurement state changes via custom events
    document.addEventListener('measurementStateChanged', () => {
      // Update our button state immediately when measurement state changes
      this.#updateAddToCartButtonState();
    }, { signal });
    
    // Listen for consent changes
    document.addEventListener('consentChanged', () => {
      // Update our button state when consent changes
      this.#updateAddToCartButtonState();
    }, { signal });
  }

  /**
   * Validate deferred payment consent if component exists
   * @returns {boolean} True if consent is valid or not required
   */
  #validateDeferredPaymentConsent() {
    // Check if deferred payment consent component exists
    const consentComponent = document.querySelector('deferred-payment-consent-component');
    if (!consentComponent) {
      return true; // No consent component, validation passes
    }
    
    // Use global function if available, otherwise check component directly
    if (typeof window.validateDeferredPaymentConsent === 'function') {
      return window.validateDeferredPaymentConsent();
    }
    
    // Fallback: check if consent is given via global function
    if (typeof window.isDeferredPaymentConsentGiven === 'function') {
      return window.isDeferredPaymentConsentGiven();
    }
    
    return true; // Default to passing if we can't validate
  }

  /**
   * Check if consent is given (for popover messages - more lenient)
   * @returns {boolean} True if consent is given or not required
   */
  #isConsentGiven() {
    // First check if there's a deferred payment consent block in the DOM (even if not initialized)
    const consentBlock = document.querySelector('deferred-payment-consent-component') || 
                        document.querySelector('[data-consent-required="true"]') ||
                        document.querySelector('.deferred-payment-consent');
    
    if (!consentBlock) {
      return true; // No consent block found, consent not required
    }
    
    // Check if deferred payment consent component is initialized
    const consentComponent = document.querySelector('deferred-payment-consent-component');
    if (!consentComponent) {
      // Component exists in HTML but not initialized yet - assume consent is required
      return false;
    }
    
    // Use global function if available
    if (typeof window.isDeferredPaymentConsentGiven === 'function') {
      return window.isDeferredPaymentConsentGiven();
    }
    
    return false; // Default to requiring consent if we can't check and component exists
  }
}

// Register the custom element
if (!customElements.get('availity-insurance-warehouse-checker-component')) {
  customElements.define('availity-insurance-warehouse-checker-component', AvailityInsuranceWarehouseCheckerComponent);
}